import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { initializeApp } from "firebase/app";
import { getAuth, signInWithEmailAndPassword } from "firebase/auth";
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';

const LOGIN_BACKGROUND = '/img/site_imgs/floral_installation.jpg';
const SAGE = 'rgb(90, 107, 86)';
const SAGE_DARK = 'rgb(72, 86, 69)';

function Login() {
  // Your web app's Firebase configuration
  const firebaseConfig = {
    apiKey: "AIzaSyBZXLaaUXIQjtrSrl1W5mf_SVsYqdrGQQA",
    authDomain: "lynsis-webapp.firebaseapp.com",
    projectId: "lynsis-webapp",
    storageBucket: "lynsis-webapp.appspot.com",
    messagingSenderId: "105983041906",
    appId: "1:105983041906:web:1b4c07e1ff333b30e07deb",
    measurementId: "G-JSHK6PJZ9G"
  };
  // Initialize Firebase
  const app = initializeApp(firebaseConfig);
  const auth = getAuth();

  const navigate = useNavigate();

  const [inputs, setInputs] = useState({
    username: "",
    password: ""
  });
  const [status, setStatus] = useState("");
  const LOCAL_URL = 'http://localhost:3001';
  const LOCAL_TEST = window.location.origin == LOCAL_URL;

  useEffect(() => {
    const currentSessionToken = getCookie('__session');
    if (currentSessionToken){
      navigate('/admin');
    }
  }, []);

  const handleChange = (event) => {
    const name = event.target.name;
    const value = event.target.value;
    setInputs((values) => {
      return { ...values, [name]: value };
    });
  };

  async function setCookie(name, value, maxAge) {
    const date = new Date(Date.now() + maxAge);
    const expires = "; expires=" + date.toUTCString();

    document.cookie = name + "=" + (value || "")  + expires + "; path=/";
  }

  function getCookie(name) {
      const nameEQ = name + "=";
      const ca = document.cookie.split(';');
      for(let i = 0; i < ca.length; i++) {
          let c = ca[i];
          while (c.charAt(0)==' ') c = c.substring(1,c.length);
          if (c.indexOf(nameEQ) == 0) return c.substring(nameEQ.length,c.length);
      }
      return null;
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setStatus("submitting");
    let baseUrl = "";
    if (LOCAL_TEST) {
      baseUrl = "http://localhost:5000";
    }

    try {
      const userCredential = await signInWithEmailAndPassword(auth, inputs.username, inputs.password);
      const user = userCredential.user;

      const idToken = await user.getIdToken();
      const createSession = await fetch(`${baseUrl}/createSession`, {
        method: "GET",
        headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            withCredentials: true,
            "Access-Control-Allow-Origin": baseUrl,
            Authentication: idToken
        },
      });

      const sessionResponse = await createSession.json();
      if (sessionResponse.success){
        await setCookie('__session', sessionResponse.sessionToken, sessionResponse.options.maxAge);
        navigate("/admin");
      } else {
        setStatus("failure");
      }
    } catch (err) {
      setStatus("failure");
    }
  }

  const submitting = status === "submitting";

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', width: '100%' }}>
      <Box
        sx={{
          display: { xs: 'none', md: 'flex' },
          width: '46%',
          position: 'relative',
          flexShrink: 0,
        }}
      >
        <Box
          component="img"
          src={LOGIN_BACKGROUND}
          alt=""
          sx={{ width: '100%', height: '100%', objectFit: 'cover', position: 'absolute', inset: 0 }}
        />
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(190deg, rgba(43,43,43,0.05) 30%, rgba(43,43,43,0.7) 100%)',
          }}
        />
        <Box sx={{ position: 'relative', mt: 'auto', p: 6, color: '#fff' }}>
          <Typography sx={{ fontFamily: '"Cormorant Garamond", serif', fontSize: '2.4rem', lineHeight: 1.15 }}>
            Lynsis Event &amp; Floral
          </Typography>
          <Typography sx={{ mt: 1, opacity: 0.85, fontSize: '0.95rem', letterSpacing: '0.02em' }}>
            Studio Admin Portal
          </Typography>
        </Box>
      </Box>
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#fff',
          px: 3,
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 380 }}>
          <Box
            component="img"
            src="/img/logo.png"
            alt="Lynsis Event & Floral"
            sx={{ width: 88, height: 'auto', display: 'block', mx: 'auto', mb: 3 }}
          />
          <Typography variant="h5" align="center" sx={{ fontWeight: 700, color: '#2b2b2b' }}>
            Welcome Back
          </Typography>
          <Typography variant="body2" align="center" sx={{ mt: 0.5, mb: 3, color: '#6b6b6b' }}>
            Sign in to manage inquiries and the product catalog.
          </Typography>
          {status === "failure" && (
            <Alert severity="error" sx={{ mb: 2 }}>Incorrect email or password.</Alert>
          )}
          <Box component="form" onSubmit={handleSubmit} noValidate>
            <TextField
              fullWidth
              label="Email"
              type="email"
              name="username"
              autoComplete="email"
              value={inputs.username}
              onChange={handleChange}
              margin="normal"
              required
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
            <TextField
              fullWidth
              label="Password"
              type="password"
              name="password"
              autoComplete="current-password"
              value={inputs.password}
              onChange={handleChange}
              margin="normal"
              required
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={submitting}
              sx={{
                mt: 3,
                py: 1.3,
                borderRadius: 2,
                textTransform: 'none',
                fontSize: '1rem',
                bgcolor: SAGE,
                '&:hover': { bgcolor: SAGE_DARK },
              }}
            >
              {submitting ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : 'Sign In'}
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export { Login };
